import isEmpty from 'lodash/isEmpty';
import {
  useGetCrmFields,
  useGetMonetizeNowCustomFields,
  useGetUserDefinedCrmFieldMappings,
} from '../../../../../../api/fieldMappingService';
import { MFlex, MSkeleton, MText } from '../../../../../../components/Monetize';
import {
  buildPlaceholderCrmFields,
  mapExistingFieldMapping,
  mapExistingSalesforceFieldMappingFromMn,
  mapMatchingSalesforceFields,
} from '../../../../../../utils/crmFieldMapping';
import { CrmFieldMappingAccordion } from '../fieldmapping/CrmFieldMappingAccordion';
import { CrmFieldsUnavailableAlert } from '../fieldmapping/CrmFieldsUnavailableAlert';

export const SalesforceFieldMapping = () => {
  const {
    data: fetchedSalesforceFields,
    isLoading: isSalesforceFieldLoading,
    isError: isSalesforceFieldsError,
  } = useGetCrmFields('salesforce');
  const { monetizeNowFields, isLoading: isMnFieldLoading } =
    useGetMonetizeNowCustomFields('salesforce');

  const {
    data: userDefinedFieldMappings,
    isLoading: isUserDefinedFieldMappingLoading,
  } = useGetUserDefinedCrmFieldMappings('salesforce');

  const salesforceFields = isSalesforceFieldsError
    ? buildPlaceholderCrmFields('salesforce', userDefinedFieldMappings)
    : fetchedSalesforceFields;

  const {
    accountToAccount,
    accountToAccountFromMn,
    contactToContact,
    opportunityToQuote,
    productToProduct,
    offeringToProduct,
  } = mapMatchingSalesforceFields(salesforceFields, monetizeNowFields);

  const {
    userDefinedUOpportunityToQuote,
    userDefinedUContactToContact,
    userDefinedUAccountToAccount,
  } = mapExistingFieldMapping(
    userDefinedFieldMappings,
    salesforceFields,
    monetizeNowFields,
  );

  const {
    userDefinedAccountToAccountFromMn,
    userDefinedProductToProduct,
    userDefinedOfferingToProduct,
  } = mapExistingSalesforceFieldMappingFromMn(
    userDefinedFieldMappings,
    salesforceFields,
    monetizeNowFields,
  );

  const isLoading =
    isSalesforceFieldLoading ||
    isMnFieldLoading ||
    isUserDefinedFieldMappingLoading;

  const loadingSkeletons = (
    <>
      <MSkeleton height={12} w="full" />
      <MSkeleton height={12} w="full" />
      <MSkeleton height={12} w="full" />
    </>
  );

  const salesforceToMonetizeNow = (
    <>
      {!isEmpty(accountToAccount) && (
        <CrmFieldMappingAccordion
          system="salesforce"
          isReadOnly={isSalesforceFieldsError}
          crmObjectType="Account"
          mnObjectType="Account"
          matchedFields={accountToAccount}
          userDefinedFields={userDefinedUAccountToAccount}
        />
      )}
      {!isEmpty(contactToContact) && (
        <CrmFieldMappingAccordion
          system="salesforce"
          isReadOnly={isSalesforceFieldsError}
          crmObjectType="Contact"
          mnObjectType="Contact"
          matchedFields={contactToContact}
          userDefinedFields={userDefinedUContactToContact}
        />
      )}
      {!isEmpty(opportunityToQuote) && (
        <CrmFieldMappingAccordion
          system="salesforce"
          isReadOnly={isSalesforceFieldsError}
          crmObjectType="Opportunity"
          mnObjectType="Quote"
          matchedFields={opportunityToQuote}
          userDefinedFields={userDefinedUOpportunityToQuote}
        />
      )}
    </>
  );

  const monetizeNowToSalesforce = (
    <>
      {!isEmpty(accountToAccountFromMn) && (
        <CrmFieldMappingAccordion
          system="salesforce"
          isReadOnly={isSalesforceFieldsError}
          direction="mnToCrm"
          crmObjectType="Account"
          mnObjectType="Account"
          matchedFields={accountToAccountFromMn}
          userDefinedFields={userDefinedAccountToAccountFromMn}
        />
      )}
      {!isEmpty(offeringToProduct) && (
        <CrmFieldMappingAccordion
          system="salesforce"
          isReadOnly={isSalesforceFieldsError}
          direction="mnToCrm"
          crmObjectType="Product2"
          mnObjectType="Offering"
          matchedFields={offeringToProduct}
          userDefinedFields={userDefinedOfferingToProduct}
        />
      )}
      {!isEmpty(productToProduct) && (
        <CrmFieldMappingAccordion
          system="salesforce"
          isReadOnly={isSalesforceFieldsError}
          direction="mnToCrm"
          crmObjectType="Product2"
          mnObjectType="Product"
          matchedFields={productToProduct}
          userDefinedFields={userDefinedProductToProduct}
        />
      )}
    </>
  );

  return (
    <MFlex gap="6" direction="column">
      <MFlex color="tPurple.dark" direction="inherit" gap="2">
        <MText fontWeight="semibold">Salesforce Data Mapping</MText>
        <MText>
          There are some Salesforce data points that are equivalent to
          MonetizeNow data points. You can map the data between Salesforce and
          MonetizeNow for accurate importing and syncing.
        </MText>
      </MFlex>
      {!isLoading && isSalesforceFieldsError && (
        <CrmFieldsUnavailableAlert crmLabel="Salesforce" />
      )}
      <MFlex direction="column" gap="4">
        <MText fontWeight="semibold" color="tPurple.dark">
          Salesforce to MonetizeNow
        </MText>
        <MFlex direction="column" gap="4">
          {isLoading ? loadingSkeletons : salesforceToMonetizeNow}
        </MFlex>
      </MFlex>
      <MFlex direction="column" gap="4">
        <MText fontWeight="semibold" color="tPurple.dark">
          MonetizeNow to Salesforce
        </MText>
        <MFlex direction="column" gap="4">
          {isLoading ? loadingSkeletons : monetizeNowToSalesforce}
        </MFlex>
      </MFlex>
    </MFlex>
  );
};
